/**
 * SPEC-AdminImpersonation §"Write-failure handling" — shared error listener.
 *
 * Subscribes to the global MutationCache. On any failed mutation whose
 * `Error.message` includes the server's `read_only_impersonation` code,
 * surfaces a toast: "Read-only mode — toggle writes on to submit."
 *
 * This is the SPEC's "shared mutation wrapper" — implemented as a single
 * cache subscription rather than wrapping every `useMutation` hook, so
 * existing hooks pick up the behaviour without changes.
 *
 * The check is loose-substring rather than strict-JSON-parse because
 * `apiRequest` / `throwIfResNotOk` (in `lib/queryClient.ts`) throws an
 * Error whose message is the body text — for our 403 response that text is
 *   `403: {"error":"read_only_impersonation","message":"..."}`
 * which trivially contains the code string.
 */

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

const READ_ONLY_CODE = "read_only_impersonation";

export function ImpersonationErrorListener() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    const cache = queryClient.getMutationCache();
    const unsubscribe = cache.subscribe((event) => {
      // React Query emits various event types; we only care about a mutation
      // that just transitioned into the `error` state.
      if (event.type !== "updated") return;
      const status = (event as any).mutation?.state?.status;
      if (status !== "error") return;
      const err = (event as any).mutation?.state?.error;
      const message: string | undefined =
        typeof err?.message === "string" ? err.message : undefined;
      if (!message) return;
      if (!message.includes(READ_ONLY_CODE)) return;
      toast({
        title: "Read-only mode",
        description: "Toggle writes on to submit.",
        variant: "destructive",
      });
    });
    return () => {
      unsubscribe();
    };
  }, [queryClient, toast]);

  return null;
}

export default ImpersonationErrorListener;
